import React, { useState, useEffect } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft, faChevronRight, faEye, faSearch, faXmark,
  faBuilding, faCircleCheck, faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// ─────────────────────────────────────────────────────────────────────────────
//  Bureau listing — the admin panel's first sight of a matchmaker.
//
//  WHY THE VERIFY ACTION IS THE POINT OF THIS SCREEN. hostStatus gates, in the
//  model's own words, "payouts, public listing and hosting". Before this module
//  nothing in the entire codebase could WRITE that field, so every bureau sat
//  permanently at `unverified` and no matchmaker could ever host a Meet or be
//  paid. The whole 70/30 commission model sat downstream of a field with no
//  writer. This row's Verify button is that writer.
//
//  The gender counts are computed by the SAME exported genderNeed() the
//  matchmaker's own console uses, so admin and matchmaker can never disagree
//  about whether she can open a Meet.
// ─────────────────────────────────────────────────────────────────────────────

const HOST_MAP = {
  unverified: { cls: "bg-gray-100 text-gray-600", label: "Unverified" },
  verified:   { cls: "bg-green-100 text-green-700", label: "Verified" },
  trusted:    { cls: "bg-amber-100 text-amber-700", label: "Trusted" },
};
const KYC_MAP = {
  NONE:     { cls: "bg-gray-100 text-gray-500", label: "No KYC" },
  PENDING:  { cls: "bg-blue-100 text-blue-700", label: "KYC Pending" },
  VERIFIED: { cls: "bg-green-100 text-green-700", label: "KYC Verified" },
  REJECTED: { cls: "bg-red-100 text-red-600", label: "KYC Rejected" },
};

const BureauListing = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [hostFilter, setHostFilter] = useState("");
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(false);
  const limit = 25;

  // Verify modal. A reason is mandatory server-side for any promotion, because
  // "who verified this bureau and why" is the first question asked when money
  // reaches the wrong account.
  const [target, setTarget] = useState(null);
  const [nextHost, setNextHost] = useState("");
  const [nextKyc, setNextKyc] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();
    const fetchRows = async () => {
      setLoading(true);
      const params = { page, limit };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (hostFilter) params.hostStatus = hostFilter;
      try {
        const res = await func.get(urls.LIST_PARTNERS_URL, { params, signal: controller.signal });
        if (res?.status === 200) {
          setRows(res?.data?.partners || []);
          setTotal(res?.data?.total || 0);
        } else {
          toast.error("Error fetching bureaus");
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        toast.error("Network error");
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(fetchRows, searchTerm ? 300 : 0);
    return () => { clearTimeout(t); controller.abort(); };
  }, [page, searchTerm, hostFilter, reload]);

  const openVerify = (row) => {
    setTarget(row);
    setNextHost(row.hostStatus);
    setNextKyc(row.kycStatus);
    setReason("");
  };

  const saveStatus = async () => {
    if (!target) return;
    const promoting = (nextHost && nextHost !== "unverified") || nextKyc === "VERIFIED";
    if (promoting && !reason.trim()) {
      toast.error("A reason is required — this opens payouts and public listing.");
      return;
    }
    setSaving(true);
    try {
      const res = await func.put(`${urls.PARTNER_STATUS_URL}${target._id}/status`, {
        hostStatus: nextHost,
        kycStatus: nextKyc,
        panVerified: nextKyc === "VERIFIED",
        reason: reason.trim(),
      });
      if (res?.status === 200) {
        toast.success(`${target.bureauName || target.displayName} updated`);
        setTarget(null);
        setReload((r) => !r);
      } else {
        toast.error(res?.data?.message || "Could not update");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Network error");
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <FontAwesomeIcon icon={faBuilding} className="text-amber-600" />
            Bureaus
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Matchmakers on the platform. Verifying one opens hosting, public listing and payouts.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-3 text-gray-400 text-sm" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            placeholder="Name, bureau, slug or city"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>
        <select
          value={hostFilter}
          onChange={(e) => { setHostFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All statuses</option>
          <option value="unverified">Unverified</option>
          <option value="verified">Verified</option>
          <option value="trusted">Trusted</option>
        </select>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Bureau</th>
              <th className="text-left px-4 py-3">City</th>
              <th className="text-left px-4 py-3">Book</th>
              <th className="text-left px-4 py-3">Balance</th>
              <th className="text-left px-4 py-3">Intake</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            )}
            {!loading && !rows.length && (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-gray-400">
                No bureaus yet. The first one is onboarded by hand.
              </td></tr>
            )}
            {!loading && rows.map((r) => {
              const host = HOST_MAP[r.hostStatus] || HOST_MAP.unverified;
              const kyc = KYC_MAP[r.kycStatus] || KYC_MAP.NONE;
              return (
                <tr key={r._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{r.bureauName || r.displayName}</div>
                    <div className="text-xs text-gray-500">{r.displayName} · /@{r.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{r.city || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="text-gray-800 font-medium">{r.clients}</span>
                    <span className="text-xs text-gray-500 ml-1">({r.female}♀ / {r.male}♂)</span>
                  </td>
                  <td className="px-4 py-3">
                    {/* The same sentence her own console shows her. */}
                    {r.need
                      ? <span className="text-amber-700 text-xs">
                          {r.need.count} more {r.need.gender === "Male" ? "grooms" : "brides"}
                        </span>
                      : r.clients
                        ? <span className="text-green-700 text-xs">Balanced</span>
                        : <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {r.confirmedIntakes} done
                    {r.openInvites ? <span className="text-amber-700"> · {r.openInvites} open</span> : null}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1 items-start">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${host.cls}`}>{host.label}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${kyc.cls}`}>{kyc.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => navigate(`/dashboard/bureau/view/${r._id}`)}
                        className="p-2 text-gray-500 hover:text-amber-600" title="Open">
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        onClick={() => openVerify(r)}
                        className="px-3 py-1.5 text-xs rounded-lg border border-amber-500 text-amber-700 hover:bg-amber-50">
                        Verify
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-3 mt-4 text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="p-2 disabled:text-gray-300"><FontAwesomeIcon icon={faChevronLeft} /></button>
          <span className="text-gray-600">{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
            className="p-2 disabled:text-gray-300"><FontAwesomeIcon icon={faChevronRight} /></button>
        </div>
      )}

      {target && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-5">
            <div className="flex items-start justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-800">
                {target.bureauName || target.displayName}
              </h2>
              <button onClick={() => setTarget(null)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <FontAwesomeIcon icon={faTriangleExclamation} className="text-amber-600 mt-0.5" />
              <p className="text-xs text-amber-800">
                Verifying opens payouts, public listing and the ability to host a Meet.
                Every change here is written to the audit log with your name.
              </p>
            </div>

            <label className="block text-xs text-gray-500 mb-1">Host status</label>
            <select value={nextHost} onChange={(e) => setNextHost(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3">
              <option value="unverified">Unverified — tools only, no payouts or hosting</option>
              <option value="verified">Verified — payouts, storefront, co-hosted Meets</option>
              <option value="trusted">Trusted — may self-host</option>
            </select>

            <label className="block text-xs text-gray-500 mb-1">KYC</label>
            <select value={nextKyc} onChange={(e) => setNextKyc(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3">
              <option value="NONE">None</option>
              <option value="PENDING">Pending</option>
              <option value="VERIFIED">Verified — PAN checked</option>
              <option value="REJECTED">Rejected</option>
            </select>

            <label className="block text-xs text-gray-500 mb-1">
              Reason {(nextHost !== "unverified" || nextKyc === "VERIFIED") && <span className="text-red-500">*</span>}
            </label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="What was checked, and how"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4" />

            <div className="flex justify-end gap-2">
              <button onClick={() => setTarget(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={saveStatus} disabled={saving}
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2">
                <FontAwesomeIcon icon={faCircleCheck} />
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BureauListing;
